import type {
  AllHTMLAttributes,
  ComponentType,
  FunctionComponent,
  PropsWithChildren,
  Ref,
} from 'react';
import { useCallback, useRef } from 'react';

import type { Placement } from '@atlaskit/popper';
import { Tooltip } from '@trello/nachos/tooltip';

import { CustomTooltip } from './CustomTooltip';
import { TooltipContent } from './TooltipContent';

type PositionType = Placement | 'mouse';

export const WorkspaceUserLimitUpsellTooltip: FunctionComponent<
  PropsWithChildren<{
    workspaceId: string;
    onCtaClick?: () => void;
    tag?:
      | ComponentType<
          AllHTMLAttributes<HTMLElement> & {
            ref: Ref<HTMLElement>;
          }
        >
      | keyof JSX.IntrinsicElements;
    position?: PositionType;
  }>
> = ({
  children,
  workspaceId,
  onCtaClick,
  tag = 'span',
  position = 'right-start',
}) => {
  const hideTooltipRef = useRef<(...args: never[]) => void>(() => {});

  const handleCtaClick = useCallback(() => {
    onCtaClick?.();
    hideTooltipRef.current();
  }, [onCtaClick]);

  const getTooltipContent = useCallback(
    () => (
      <TooltipContent onCtaClick={handleCtaClick} workspaceId={workspaceId} />
    ),
    [workspaceId, handleCtaClick],
  );

  return (
    <Tooltip
      content={getTooltipContent}
      // @ts-expect-error component needs type ForwardRefExoticComponent<TooltipPrimitiveProps>
      component={CustomTooltip}
      tag={tag}
      position={position}
      hideTooltipOnClick
    >
      {({ onClick, ...tooltipProps }) => {
        // There is currently no way to programatically hide the tooltip so we
        // need to specify hideTooltipOnClick and then store a reference to the
        // tooltip's onClick function so that we can call it when we want to hide
        // the tooltip... ¯\_(ツ)_/¯
        hideTooltipRef.current = onClick;

        return <span {...tooltipProps}>{children}</span>;
      }}
    </Tooltip>
  );
};
